/**
 * Phần tính toán thuần của màn Tài khoản nhân viên (FAC-26417 FR-001 bước 8).
 *
 * Tách khỏi component vì trong repo này không file test nào import một component nào — xem
 * `helpers/actualHours.js`.
 */

/** Khoá một dòng đổi tài khoản. Một chỗ sinh ra, để bảng và payload không tự ghép hai kiểu. */
export const changeKey = (employeeCode, actor) => `${employeeCode}|${actor}`

/**
 * Ngày áp dụng của từng dòng đổi: ô người dùng sửa tại chỗ, nếu không thì mốc chung.
 *
 * ⚠️ Mốc theo TỪNG DÒNG chứ không một ngày cho cả file. Một lượt nhập thường chỉ đổi vài dòng trong
 *    số vài chục, và mỗi dòng đổi vào một ngày khác: người A chuyển công đoạn từ 15/08, người B từ
 *    01/10. Bắt khai một ngày cho tất cả là bắt chọn một con số sai cho mọi dòng trừ một.
 */
/**
 * Mốc hiệu lực THẬT SỰ sẽ dùng cho dòng không tự khai `Hiệu lực từ`.
 *
 * ⚠️ Một nguồn duy nhất cho BA chỗ phải nói cùng một con số: câu cảnh báo "những dòng này sẽ lấy
 *    mốc …", cột "Áp dụng từ" của bảng xem trước, và `appliedFrom` gửi lên service. Trước đây câu
 *    cảnh báo đọc `defaultAppliedFrom` (HÔM NAY) còn service lại rơi về mốc mặc định của nó — màn
 *    hình hứa một ngày, DB ghi một ngày khác, và không chỗ nào báo lệch.
 *
 * ⚠️ `defaultEffectiveFrom` chứ KHÔNG phải `defaultAppliedFrom`. Cái sau là mốc đề xuất cho việc
 *    ĐÓNG tài khoản (hôm nay là đúng), cái này là mốc MỞ hiệu lực cho dòng thiếu ngày. Lẫn hai cái
 *    là mọi dòng mới chỉ có hiệu lực từ hôm nay, và chấm công của ba tháng trước mất tài khoản.
 *
 * `picked` rỗng = người dùng chưa chọn → KHÔNG gửi `appliedFrom` lên, để service dùng mốc của nó.
 */
export const effectiveAppliedFromOf = (preview, picked) =>
    picked || (preview && preview.defaultEffectiveFrom) || null

/**
 * Mốc THẬT SỰ áp cho dòng ĐỔI TÀI KHOẢN — khác mốc của dòng thêm mới, và phải khác.
 *
 * ⚠️ Service dùng HAI mốc mặc định (xem `fromOf` ở `worker-accounts.service.ts`):
 *      · thêm mới → `defaultEffectiveFrom` (01/08/2026), để chấm công kỳ trước quy được về;
 *      · đổi tài khoản → NGÀY ÁP DỤNG (mặc định hôm nay), vì nhánh này ĐÓNG bản đang chạy và lùi
 *        mốc là viết lại những tháng đã chạy đúng — `assertAfter` từ chối thẳng.
 *
 * Dùng nhầm `effectiveAppliedFromOf` cho bảng đổi tài khoản thì cột "Áp dụng từ" hiện 01/08 trong
 * khi DB ghi hôm nay; và vì `overridePayloadOf` lọc bỏ dòng trùng mốc mặc định, cái ngày đang hiện
 * cũng không được gửi lên để sửa lại. Màn hình nói một đằng, dữ liệu một nẻo. (Vòng review 03/11/2026.)
 */
export const changeAppliedFromOf = (preview, picked) =>
    picked || (preview && preview.defaultAppliedFrom) || null

/**
 * Mốc THẬT SỰ áp cho một dòng của bảng đổi tài khoản — phải khớp `fromOf` của service.
 *
 * Thứ tự ưu tiên ở service: ô sửa tay trên bản xem trước → cột `Hiệu lực từ` CỦA CHÍNH DÒNG trong
 * file → `dto.appliedFrom` → mốc mặc định.
 *
 * ⚠️ Vế giữa (`row.effectiveFrom`) từng bị bỏ quên ở đây. Hệ quả: file có khai `Hiệu lực từ` thì
 *    cột "Áp dụng từ" hiện HÔM NAY trong khi DB ghi đúng ngày trong file — màn hình nói một đằng,
 *    dữ liệu một nẻo, và người soát không có cách nào biết mình đang nhìn số sai. (Vòng review 2,
 *    G-R4.)
 */
export const effectiveFromOf = (row, overrides, fallback) => {
    const key = changeKey(row.employeeCode, row.actor)

    return (overrides && overrides[key]) || row.effectiveFrom || fallback || null
}

/**
 * `overrides` dạng bảng → mảng payload: CHỈ những ô người dùng TỰ GÕ.
 *
 * ⚠️ Vị từ là "có ô sửa tay hay không", KHÔNG phải "giá trị có khác mốc chung hay không". Bản trước
 *    lọc `!== fallback`, nên khi người dùng đặt ô của một dòng về ĐÚNG mốc chung — thao tác hoàn
 *    toàn tự nhiên, "cho dòng này dùng cùng ngày với phần còn lại" — dòng đó bị cắt khỏi payload,
 *    service rơi xuống vế `row.effectiveFrom` và ghi ngày của FILE. Màn hình nói 05/10, DB ghi
 *    20/09, không gì báo. Đo qua cả hai bản thật: lệch 1/12 ca. (Vòng review 7, #11.)
 *
 * Mốc chung đi riêng bằng `appliedFrom`; mốc khai trong file thì service tự đọc. Nên danh sách này
 * chỉ cần mang đúng phần người dùng ghi đè, và nhật ký kiểm toán vì thế cũng chỉ còn phần đó.
 */
export const overridePayloadOf = (changed, overrides) =>
    (changed || [])
        .map((row) => ({
            employeeCode: row.employeeCode,
            actor: row.actor,
            // CHỈ ô người dùng tự gõ. Mốc chung đi bằng `appliedFrom`, mốc của file service tự đọc.
            effectiveFrom: (overrides && overrides[changeKey(row.employeeCode, row.actor)]) || null,
        }))
        .filter((entry) => entry.effectiveFrom)

/**
 * Lượt nhập này có ĐỤNG gì không — gồm cả dòng chỉ sửa tên/bộ phận.
 *
 * ⚠️ Lấy `changeCount` của service, KHÔNG tự cộng `added + changed`: dòng chỉ đổi tên nằm ở
 *    `renamed` và service là bên biết tên đang lưu là gì. Tự cộng thì file sửa vài cái tên bị coi
 *    là "không có gì", nút Nhập biến mất và người dùng không có cách nào sửa một cái tên gõ sai.
 *
 * Dùng để ẨN nút Nhập: file trùng khít từng ký tự thì bấm Nhập chỉ sinh một dòng nhật ký rỗng, và
 * một nút luôn hiện dù không làm gì là nút người dùng học cách bấm cho qua.
 */
export const changeCountOf = (preview) => {
    if (!preview) return 0

    if (typeof preview.changeCount === 'number') return preview.changeCount

    return (
        (preview.added || []).length +
        (preview.changed || []).length +
        (preview.renamed || []).length +
        (preview.closing || []).length
    )
}

/**
 * Nhập được hay không, kèm lý do khi không.
 *
 * ⚠️ `unchangedCount > 0` mà không có thay đổi nào VẪN cho nhập: lượt đó cập nhật tên và bộ phận tại
 *    chỗ (service `touched`), và đó là cách duy nhất sửa một cái tên gõ sai. Chỉ chặn khi file không
 *    đọc được dòng nào.
 */
export const accountImportBlockerOf = (preview, confirmClose) => {
    if (!preview) return 'Chưa chọn file.'

    if ((preview.readRows || 0) === 0) {
        return `Không đọc được dòng nào từ sheet "${preview.sheet || '3D'}" — kiểm tra lại đúng file chưa.`
    }

    /*
     * ⚠️ Trùng mã mà khác tài khoản = ĐÓNG tài khoản cũ, và việc đó không hoàn tác được bằng cách
     *    nhập lại file (dòng cũ đã `expired`). Có thể là chuyển tổ, cũng có thể là gõ nhầm tên đăng
     *    nhập — dữ liệu không phân biệt được, nên phải hỏi.
     *
     *    Service cũng chặn (`confirmClose`), đây chỉ là chỗ hỏi cho tử tế. Chặn ở MỘT mình FE thì
     *    caller không qua màn hình bỏ qua được; chặn ở một mình service thì người dùng nhận một
     *    lỗi 400 sau khi đã chờ nhập xong 2.000 dòng.
     */
    if (accountsToCloseOf(preview).length > 0 && !confirmClose) {
        return 'Lượt nhập này sẽ ĐÓNG hiệu lực một số tài khoản đang dùng — xác nhận bên dưới trước khi nhập.'
    }

    return null
}

/**
 * Tài khoản đang chạy sẽ MẤT hiệu lực sau lượt nhập, và vì sao.
 *
 * ⚠️ Chỉ lấy `reason: 'changed'` — tức ca trùng mã mà khác tài khoản. Dòng `reason: 'effectiveTo'`
 *    là người dùng tự khai mốc đóng NGAY TRONG FILE, tức đã trả lời xong câu hỏi này; bắt tick
 *    thêm lần nữa là hỏi lại thứ họ vừa nói. Service cũng phân biệt đúng như vậy.
 */
export const accountsToCloseOf = (preview) =>
    ((preview && preview.willClose) || []).filter((entry) => entry.reason === 'changed')

/** Tài khoản bị đóng vì chính file khai `Hiệu lực đến` — chỉ BÁO, không hỏi. */
export const accountsClosingByFileOf = (preview) =>
    ((preview && preview.willClose) || []).filter((entry) => entry.reason === 'effectiveTo')

/**
 * Dòng không khai `Hiệu lực từ` — sẽ lấy mốc mặc định của service.
 *
 * ⚠️ Phải bày ra TRƯỚC khi ghi. Mốc mặc định quyết định chấm công từ kỳ nào quy được về tài khoản
 *    đó: chọn hộ một ngày rồi im lặng là âm thầm quyết định hộ người dùng một chuyện họ kiểm được.
 */
export const accountsWithoutEffectiveFromOf = (preview) => (preview && preview.missingEffectiveFrom) || []

/**
 * Các mã giữ NHIỀU HƠN MỘT tài khoản sau lượt nhập này.
 *
 * ⚠️ Lấy từ service, tính trên TOÀN BỘ dòng của file — không chỉ phần đang đổi. Ca đã gặp thật:
 *    file trùng khít bản đang dùng nên không có `added`/`changed` nào, mà `00607` vẫn đang giữ hai
 *    tài khoản; tính theo phần đang đổi thì cảnh báo biến mất đúng lúc người dùng mở màn hình ra xem.
 *
 * ⚠️ Phải nói ra TRƯỚC khi ghi: chấm công cho MỘT con số giờ có mặt mỗi ngày, chia đôi cho hai tài
 *    khoản là bịa còn nhân đôi vào cả hai là thổi mẫu số gấp đôi. Service sẽ bỏ qua những dòng đó,
 *    và người dùng cần biết trước chứ không phải sau khi nhập xong 2.000 dòng mới thấy thiếu.
 */
export const ambiguousAfterImport = (preview) => (preview && preview.ambiguous) || []

/** Dòng này thuộc một mã sẽ giữ nhiều hơn một tài khoản — tức chấm công của mã đó sẽ bị bỏ qua. */
export const ambiguousCodeSet = (preview) =>
    ambiguousAfterImport(preview).reduce((acc, entry) => ({...acc, [entry.employeeCode]: true}), {})

/**
 * Bảng dòng sẽ ghi, theo chế độ xem.
 *
 * ⚠️ Con số tóm tắt KHÔNG thay được bảng. "62 thêm mới" không nói được tài khoản nào gắn với ai, mà
 *    đây là lượt ghi quyết định toàn bộ sản lượng và giờ công quy về người nào — soát sai một dòng
 *    là một người nhận số của người khác suốt các kỳ sau. Người dùng phải đọc được từng dòng trước
 *    khi bấm, giống hệt bảng xem trước của nhập chấm công.
 */
export const accountPreviewRowsOf = (preview, mode) => {
    const added = (preview && preview.added) || []
    const changed = (preview && preview.changed) || []
    /*
     * ⚠️ `retimed` PHẢI có mặt ở đây. Service tách nhánh này ra riêng chính VÌ nó là việc nguy hiểm
     *    nhất của lượt nhập — thu hẹp khoảng hiệu lực của một tài khoản là đẩy chấm công TRƯỚC mốc
     *    mới sang `no_account`. Bản đầu của bản vá F4 chỉ làm phía BE: dòng rời `unchanged` sang
     *    `retimed`, rơi khỏi mọi ô thống kê và khỏi bảng này, nên màn hình hiện
     *    "Thêm mới 0 · Đổi tài khoản 0 · Giữ tài khoản 0" với bảng RỖNG, trong khi nút lại sáng
     *    "Nhập 2 thay đổi" (`changeCount` của server ĐÃ cộng `retimed`).
     *
     *    Tức F4 chỉ đổi triệu chứng từ *không ghi gì* sang *ghi mà không bày ra* — đúng ca `00607`
     *    mà cả tính năng sinh ra để gỡ. (Vòng review 5, #3.)
     */
    const retimed = (preview && preview.retimed) || []

    /*
     * ⚠️ Gắn NHÃN NHÓM vào từng dòng. Không có nó thì cột "Ghi chú" phải ĐOÁN nhóm từ hình dạng dữ
     *    liệu, và nó đoán sai theo hướng nguy hiểm nhất: dòng `retimed` không có `previousActors`
     *    nên rơi vào nhánh mặc định và hiện thẻ XANH "Thêm mới". Tức việc duy nhất trong lượt nhập
     *    làm ĐỔI CÁCH QUY ĐỔI của dữ liệu đã có lại được bày ra như một tài khoản mới vô hại.
     *    (Vòng review 2, R1.)
     *
     *    Gắn ở ĐÂY chứ không ở component: `accountPreviewRowsOf` là chỗ duy nhất biết dòng đến từ
     *    nhóm nào, và nó test được.
     */
    const tag = (rows, kind) => rows.map((row) => ({...row, changeKind: kind}))

    if (mode === 'added') return tag(added, 'added')
    if (mode === 'changed') return tag(changed, 'changed')
    if (mode === 'retimed') return tag(retimed, 'retimed')

    // Mặc định: thêm mới trước, đổi tài khoản, rồi sửa mốc. Trong mỗi nhóm giữ NGUYÊN thứ tự dòng
    // trong file — thứ duy nhất đối chiếu được với Excel đang mở bên cạnh.
    const all = [...tag(added, 'added'), ...tag(changed, 'changed'), ...tag(retimed, 'retimed')]

    if (mode !== 'ambiguous') return all

    /*
     * Các dòng mà chấm công SẼ BỊ BỎ QUA vì mã đó giữ nhiều hơn một tài khoản.
     *
     * ⚠️ Lọc theo MÃ chứ không theo dòng: cả hai (hay nhiều) dòng của mã đó đều phải hiện ra cạnh
     *    nhau, vì việc người dùng cần làm là CHỌN giữ tài khoản nào — thấy mỗi một dòng thì không
     *    có gì để so. Đây cũng là lý do bộ lọc này tồn tại: trong 62 dòng thì hai dòng của `00607`
     *    rơi vào hai trang khác nhau và gần như không thể nhìn thấy cùng lúc.
     */
    const codes = ambiguousCodeSet(preview)

    return all.filter((row) => codes[row.employeeCode])
}


/**
 * Chuẩn hoá chuỗi để tìm kiếm không phụ thuộc DẤU và HOA-THƯỜNG.
 *
 * ⚠️ Bỏ dấu là bắt buộc, không phải tiện nghi: người dùng gõ `tu` để tìm "Nguyễn Trọng Tú" và gõ
 *    `dong goi` để lọc "Đóng gói". So khớp nguyên văn thì hộp tìm kiếm im lặng không ra gì và trông
 *    như hỏng. Cùng luật `normalizeStageName` phía service (`src/domain/routing.ts`), kể cả vế
 *    `đ → d` — tiếng Việt tách NFD KHÔNG tách được chữ đ.
 */
export const normalizeText = (value) => String(value === null || value === undefined ? '' : value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, 'd')
    .toLowerCase()
    .trim()

/**
 * Lọc bảng theo ô tìm kiếm và bộ phận.
 *
 * Tìm trên CẢ bốn cột (mã, họ tên, bộ phận, tài khoản) thay vì bắt chọn cột trước: người dùng gõ
 * `00607` hay `tunt` hay `Tú` đều là đang tìm đúng một người, và hỏi họ "tìm theo cột nào" là bắt
 * trả lời một câu họ không quan tâm.
 */
export const searchAccountRows = (rows, term, department) => {
    const needle = normalizeText(term)

    return (rows || []).filter((row) => {
        if (department && row.department !== department) return false
        if (needle === '') return true

        return [row.employeeCode, row.fullName, row.department, row.actor]
            .some((field) => normalizeText(field).indexOf(needle) >= 0)
    })
}

/** Các bộ phận có mặt trong tập dòng, đã sắp xếp — nguồn cho ô lọc. */
export const departmentsOf = (rows) =>
    [...new Set((rows || []).map((row) => row.department).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi'))
